import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SorterComponent } from './sorter/sorter.component';
import { DatePipe, NgClass } from '@angular/common';
import { BeraterService } from '../../services/berater.service';
import { BonusActionsComponent } from './bonus-actions/bonus-actions.component';
import { BeraterUebersicht } from '../../shared/model/beraterUebersicht.model';
import { BeraterUebersichtService } from '../../services/beraterUebersicht.service';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  createFilterOptions,
  FilterOptions,
} from '../../shared/model/filter.model';
import { SorterService } from '../../services/sorter.service';
import { KatalogeService } from '../../services/kataloge.service';
import { Katalog } from '../../shared/model/katalog.model';
import { FaehigkeitenService } from '../../services/faehigkeiten.service';
import { IFaehigkeit } from '../../shared/model/faehigkeit.model';
import { MessageService } from '../../shared/components/message.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TravelWillingnessPipe } from '../../shared/travel-willingness.pipe';
import { ConsultantNamePipe } from '../../shared/consultant-name.pipe';
import { MatCard, MatCardContent } from '@angular/material/card';
import { UserInformationComponent } from '../../shared/components/user-information/user-information.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { UserFilterStateService } from '../../services/userFilterState';
import { RequestsService } from '../../services/requests.service';
import { Requests, RequestType } from '../../shared/model/request.model';
import { UserlistFilterComponent } from './userlist-filter/userlist-filter.component';
import { Advisor } from '../../shared/model/advisor.model';
import { AdvisorService } from '../../services/advisor.service';
import { LazyImageDirective } from '../../shared/lazy-image.directive';
import { CompanyBadgeComponent } from '../../shared/components/company-badge/company-badge.component';
import { ProfilerLanguageFlagComponent } from '../../shared/components/profiler-language-flag/profiler-language-flag.component';
import {
  ProfilerLanguage,
  profilerLanguageLabelKey,
  toProfilerLanguage,
} from '../../shared/constants/profiler-language';
import {
  consultantNameTexts,
  consultantOtherTexts,
  consultantPositionTexts,
} from './consultant-search-text';
import {
  ConsultantSearchFields,
  matchesScope,
  searchTerms,
} from './consultant-search-match';
import {
  DEFAULT_SEARCH_SCOPE,
  SearchScope,
  toSearchScope,
  toSearchTerm,
} from './search-scope';
import { VocabularyService } from '../../services/vocabulary.service';
import { vocabularyName } from '../../shared/model/vocabulary-name';
import { languageDisplayName } from '../../shared/constants/languages';
import { CompanyFilter } from '../../shared/model/company.model';
import { CompanyService } from '../../services/company.service';
import { combineLatest, forkJoin } from 'rxjs';
import { loadAllPages } from '../../shared/load-all-pages';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserRoleService } from '../../services/userRole.service';
import {
  CompletenessLevel,
  HIRE_DATE_ROLES,
  ProfileCompleteness,
  completenessLevel,
  computeProfileCompleteness,
  fromBeraterUebersicht,
} from '../../shared/model/profile-completeness';
import {
  CompletenessCounts,
  emptyCompletenessCounts,
} from './completeness-filter/completeness-filter.component';
import { CompletenessMeterComponent } from './completeness-meter/completeness-meter.component';

function toCompanyFilter(value: unknown): CompanyFilter {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed || 'ALL';
}

@Component({
  selector: 'app-user-list',
  templateUrl: './userlist.component.html',
  styleUrls: ['./userlist.component.scss'],
  standalone: true,
  imports: [
    SorterComponent,
    NgClass,
    BonusActionsComponent,
    MatCheckbox,
    FormsModule,
    TranslatePipe,
    TravelWillingnessPipe,
    ConsultantNamePipe,
    ReactiveFormsModule,
    MatCard,
    MatCardContent,
    UserInformationComponent,
    DatePipe,
    MatProgressSpinner,
    UserlistFilterComponent,
    RouterLink,
    LazyImageDirective,
    CompanyBadgeComponent,
    ProfilerLanguageFlagComponent,
    CompletenessMeterComponent,
  ],
})
export class UserListComponent implements OnInit {
  allUsers: BeraterUebersicht[] = [];
  users: BeraterUebersicht[] = [];
  initialUsers: BeraterUebersicht[] = [];
  loading = true;
  resultsLoaded = false;
  error = '';
  currentSortColumn: string = '';
  archiveMode: boolean = false;
  openMenuId: string | null = null;
  openFilter: boolean = false;
  listView: boolean = false;
  allCatalogues: Katalog[] = [];
  allSkills: IFaehigkeit[] = [];
  allCertificates: string[] = [];
  allIndustries: Requests[] = [];
  allMainFocuses: Requests[] = [];
  allTrainings: Requests[] = [];
  filterAmount: number = 0;
  filter: FilterOptions = createFilterOptions();
  protected advisorOptions: Advisor[];
  searchValueString: string = '';
  searchScope: SearchScope = 'name';

  // Unsichtbare Ansage neben dem sichtbaren Zaehler: nur, solange etwas eingegeben ist.
  // Als Feld statt als Getter — der Text haengt an Trefferzahl, Bereich und Sprache und
  // wuerde sonst bei jedem Change-Detection-Lauf der ganzen Kartenliste neu uebersetzt.
  searchAnnouncement: string = '';

  selectedCompany: CompanyFilter = 'ALL';
  applicantOnly: boolean = false;
  freelancerOnly: boolean = false;
  defaultPhoto = './assets/img/no-picture.png';

  // NP20-1192: Die Ampelwerte haengen an den geladenen Daten, nicht an den Filtern.
  // Sie werden deshalb einmal je Datensatz gerechnet und gemerkt — `renderUsers()`
  // laeuft bei jedem Tastendruck in der Suche und wuerde das Regelwerk sonst bei
  // jedem Durchlauf ueber alle Berater und ihre Projekte jagen.
  private completenessById = new Map<string, ProfileCompleteness>();
  private knownIndustryNames = new Set<string>();
  private knownMainFocusNames = new Set<string>();
  completenessCounts: CompletenessCounts = emptyCompletenessCounts();
  completenessLevels: CompletenessLevel[] = [];

  constructor(
    private userOverviewService: BeraterUebersichtService,
    private userService: BeraterService,
    private sorterService: SorterService,
    private catalogeService: KatalogeService,
    private skillService: FaehigkeitenService,
    private requestsService: RequestsService,
    private messageService: MessageService,
    protected filterState: UserFilterStateService,
    private translate: TranslateService,
    private advisorService: AdvisorService,
    private companyService: CompanyService,
    private route: ActivatedRoute,
    private router: Router,
    private userRoleService: UserRoleService,
    private destroyRef: DestroyRef,
    private vocabulary: VocabularyService,
  ) {}

  ngOnInit() {
    this.getCatalog();
    this.getSkills();
    this.getCertificates();
    this.getCatalogNames();
    this.getTrainings();
    this.vocabulary.changes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sortVocabularyOptions();
        this.renderUsers();
      });

    // Die Ansage steht uebersetzt im Feld; beim Sprachwechsel zur Laufzeit muss sie neu
    // entstehen.
    this.translate.onLangChange
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateSearchAnnouncement());

    // Kopie statt Referenz: das Filter-Panel schreibt per ngModel direkt in
    // `filter`, ein geteiltes Objekt wuerde den unbestaetigten Entwurf sofort
    // als angewendeten Filter gelten lassen.
    if (this.filterState.filter) {
      this.filter = { ...this.filterState.filter };
      this.countFilterAmount();
    }
    if (this.filterState.sortColumn) {
      this.currentSortColumn = this.filterState.sortColumn;
    }
    this.completenessLevels = [...this.filterState.completenessLevels];

    // Search + toggles live in the URL (shareable / browser-back) AND are mirrored in
    // filterState (like filter/sortColumn) so they survive breadcrumb-back too, which
    // drops the query string (NP20-829). Prefer the URL when it carries state, else
    // restore from the service and reflect it back into the URL so the search box
    // (driven by the query params) and the shareable link stay in sync.
    const params = this.route.snapshot.queryParams;
    const hasUrlState =
      params['in'] != null ||
      params['q'] != null ||
      params['search'] != null ||
      params['applicant'] != null ||
      params['freelancer'] != null ||
      params['firma'] != null;
    const hadFirmaParam = params['firma'] != null;
    if (hasUrlState) {
      // Ein alter Link mit `search=` zaehlt als Angabe aus der Adresse: fehlt darin `in`,
      // gilt „Name" und nicht der gemerkte Bereich, damit ein geteilter Link bei jedem
      // dasselbe zeigt. Geschrieben wird danach nur noch `in`/`q`.
      this.searchScope = toSearchScope(params['in']);
      this.searchValueString = toSearchTerm(params['q'] ?? params['search']);
      this.applicantOnly = params['applicant'] === 'true';
      this.freelancerOnly = params['freelancer'] === 'true';
      this.selectedCompany = toCompanyFilter(params['firma']);
      this.filterState.search = this.searchValueString;
      this.filterState.searchScope = this.searchScope;
      this.filterState.applicantOnly = this.applicantOnly;
      this.filterState.freelancerOnly = this.freelancerOnly;
      this.filterState.company = this.selectedCompany;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: this.searchParams(),
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    } else {
      this.searchValueString = this.filterState.search;
      this.searchScope = toSearchScope(this.filterState.searchScope);
      this.applicantOnly = this.filterState.applicantOnly;
      this.freelancerOnly = this.filterState.freelancerOnly;
      this.selectedCompany = this.filterState.company ?? 'ALL';
      if (
        this.searchValueString ||
        this.searchScope !== DEFAULT_SEARCH_SCOPE ||
        this.applicantOnly ||
        this.freelancerOnly ||
        this.selectedCompany !== 'ALL'
      ) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            ...this.searchParams(),
            applicant: this.applicantOnly || null,
            freelancer: this.freelancerOnly || null,
            ...(this.selectedCompany !== 'ALL'
              ? { firma: this.selectedCompany }
              : {}),
          },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    }

    this.companyService.load().subscribe({
      next: () => this.normalizeSelectedCompany(hadFirmaParam),
      error: () => {},
    });
    this.getBeraterUebersicht();

    loadAllPages<Advisor>(
      (page, size) => this.advisorService.getAdvisors(page, size),
      200,
    ).subscribe({
      next: (advisors) => {
        this.advisorOptions = advisors;
      },
    });
  }

  // Bis zur ersten Antwort kein Zaehler — sonst stuende kurz „0 Ergebnisse“ da.
  get resultCount(): number | null {
    return this.resultsLoaded ? this.users.length : null;
  }

  // Die Ansage entsteht einmal je Durchlauf von `renderUsers()` und beim Sprachwechsel.
  private updateSearchAnnouncement(): void {
    const count = this.resultCount;
    if (count === null || !this.searchValueString?.trim()) {
      this.searchAnnouncement = '';
      return;
    }
    this.searchAnnouncement = this.translate.instant(
      count === 1 ? 'search.liveOne' : 'search.liveMany',
      {
        count,
        scope: this.translate.instant(`search.scope.${this.searchScope}`),
      },
    );
  }

  get resultCountKey(): string {
    return this.resultCount === 1
      ? 'userlist.content.resultCountOne'
      : 'userlist.content.resultCountMany';
  }

  // Der Archiv-Umschalter zeigt mehr statt weniger und zaehlt deshalb nicht.
  get hasRestriction(): boolean {
    return (
      !!this.searchValueString?.trim() ||
      this.selectedCompany !== 'ALL' ||
      this.applicantOnly ||
      this.freelancerOnly ||
      this.filterAmount >= 1 ||
      this.completenessLevels.length > 0
    );
  }

  // Der angewandte Filter, nicht der Entwurf im Seitenmenue.
  get activeProfilerLanguage(): ProfilerLanguage | null {
    return this.filterState.filter?.profilerLanguage ?? null;
  }

  profilerLanguageOf(user: BeraterUebersicht): ProfilerLanguage | null {
    return toProfilerLanguage(user?.langKey);
  }

  // Die Flagge in der Kachel ist praesentational — als Kind eines Elements mit
  // eigener Rolle wird ihr Text nie vorgelesen. Die Sprache gehoert deshalb in
  // das aria-label der Kachel.
  readonly profilerLanguageLabelKey = profilerLanguageLabelKey;

  cardAriaKey(language: ProfilerLanguage | null): string {
    return language
      ? 'userlist.content.cardAria'
      : 'userlist.content.cardAriaWithoutProfiler';
  }

  // Die Ampel ist als Kind der Kachel praesentational — wie die Flagge gehoert sie
  // deshalb in deren aria-label, sonst wird sie nie vorgelesen.
  completenessAria(user: BeraterUebersicht): string {
    const completeness = this.completenessById.get(user?.id);
    if (!completeness) return '';
    const badges = completeness.totalBadges;
    const badgeText =
      badges === 0
        ? this.translate.instant('userlist.completeness.noOpenBadges')
        : `${badges} ${this.translate.instant(
            badges === 1
              ? 'userlist.completeness.openBadge'
              : 'userlist.completeness.openBadges',
          )}`;
    return ` · ${this.translate.instant('userlist.completeness.cardAria', {
      percent: completeness.percent,
      badges: badgeText,
    })}`;
  }

  // Schliessen ohne Anwenden verwirft den Entwurf: sonst steht beim naechsten
  // Oeffnen eine Auswahl im Panel, die die Liste nie gesehen hat.
  onFilterPanelOpenedChange(opened: boolean) {
    this.openFilter = opened;
    if (!opened) {
      this.filter = this.filterState.filter
        ? { ...this.filterState.filter }
        : createFilterOptions();
    }
  }

  onCompanyFilterChange(value: CompanyFilter) {
    this.selectedCompany = value;
    this.filterState.company = value;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { firma: value === 'ALL' ? null : value },
      queryParamsHandling: 'merge',
    });
    this.renderUsers();
  }

  private normalizeSelectedCompany(hadFirmaParam: boolean): void {
    if (this.selectedCompany === 'ALL' && !hadFirmaParam) {
      return;
    }
    const resolved: CompanyFilter =
      this.companyService.resolveAbbreviation(
        this.selectedCompany === 'ALL' ? '' : this.selectedCompany,
      ) ?? 'ALL';
    if (resolved === this.selectedCompany && resolved !== 'ALL') {
      return;
    }
    this.selectedCompany = resolved;
    this.filterState.company = resolved;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { firma: resolved === 'ALL' ? null : resolved },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.renderUsers();
  }

  // Persisted in the URL so it survives navigation/reload (see NP20-723).
  onApplicantFilterChange(value: boolean) {
    this.applicantOnly = value;
    this.filterState.applicantOnly = value;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { applicant: value ? true : null },
      queryParamsHandling: 'merge',
    });
    this.renderUsers();
  }

  // Persisted in the URL like the applicant filter (see NP20-834).
  onFreelancerFilterChange(value: boolean) {
    this.freelancerOnly = value;
    this.filterState.freelancerOnly = value;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { freelancer: value ? true : null },
      queryParamsHandling: 'merge',
    });
    this.renderUsers();
  }

  private renderUsers(): void {
    const withoutCompleteness = this.applyCommonFilters([...this.allUsers]);
    // Die Zahlen an den Schaltern rechnen ueber die uebrigen Filter, aber ohne den
    // Ampelfilter selbst — sonst wuerden sie sich beim Klicken selbst veraendern.
    this.completenessCounts = this.countLevels(withoutCompleteness);
    this.users =
      this.completenessLevels.length === 0
        ? withoutCompleteness
        : withoutCompleteness.filter((user) => {
            const level = this.levelOf(user);
            return level !== null && this.completenessLevels.includes(level);
          });
    this.updateSearchAnnouncement();
  }

  private applyCommonFilters(users: BeraterUebersicht[]): BeraterUebersicht[] {
    let result = users;

    const terms = searchTerms(this.searchValueString);
    if (terms.length > 0) {
      result = result.filter((user) =>
        matchesScope(this.searchFieldsOf(user), this.searchScope, terms),
      );
    }

    if (this.filterState.filter) {
      result = this.sorterService.applyFilter(
        result,
        this.filterState.filter,
        this.currentSortColumn,
        (user) => this.completenessById.get(user?.id),
      );
    } else if (this.currentSortColumn) {
      result = this.sorterService.applySorting(
        result,
        this.currentSortColumn,
        (user) => this.completenessById.get(user?.id),
      );
    }

    if (this.selectedCompany !== 'ALL') {
      result = result.filter((user) => user?.firma === this.selectedCompany);
    }

    if (this.applicantOnly) {
      result = result.filter((user) => user?.applicant === true);
    }

    if (this.freelancerOnly) {
      result = result.filter((user) => user?.freelancer === true);
    }

    return result;
  }

  private countLevels(users: BeraterUebersicht[]): CompletenessCounts {
    const counts = emptyCompletenessCounts();
    users.forEach((user) => {
      const level = this.levelOf(user);
      if (level) counts[level] += 1;
    });
    return counts;
  }

  private levelOf(user: BeraterUebersicht): CompletenessLevel | null {
    const completeness = this.completenessById.get(user?.id);
    return completeness ? completenessLevel(completeness.totalBadges) : null;
  }

  /**
   * NP20-1192: Einmal je geladenem Datensatz. Liefert die Antwort die Daten fuer einen
   * Berater nicht vollstaendig, bleibt er ohne Eintrag — seine Kachel zeigt dann keine
   * Ampel statt einer geratenen Zahl.
   */
  private recomputeCompleteness(): void {
    this.completenessById = new Map<string, ProfileCompleteness>();
    const canSeeEntryDate = this.userRoleService.includesRoles(HIRE_DATE_ROLES);
    this.allUsers.forEach((user) => {
      const input = fromBeraterUebersicht(user);
      if (!input || !user?.id) return;
      this.completenessById.set(
        user.id,
        computeProfileCompleteness(input, {
          knownIndustryNames: this.knownIndustryNames,
          knownMainFocusNames: this.knownMainFocusNames,
          hireDateRequired: canSeeEntryDate && !user.applicant,
        }),
      );
    });
  }

  completenessOf(user: BeraterUebersicht): ProfileCompleteness | null {
    return this.completenessById.get(user?.id) ?? null;
  }

  onCompletenessLevelsChange(levels: CompletenessLevel[]): void {
    this.completenessLevels = levels;
    this.filterState.completenessLevels = [...levels];
    this.renderUsers();
  }

  createLanguageList(user: BeraterUebersicht) {
    const codes = this.sorterService?.normalizeUserLanguagesToCodes(
      user?.sprachen,
    );

    if (!codes || !Array.isArray(codes) || codes.length === 0) {
      return [];
    }
    return codes.map((code) =>
      languageDisplayName(code, (key) => this.translate.instant(key), this.translate.currentLang ?? 'de'),
    );
  }

  resetFilters() {
    this.openFilter = false;
    this.filter = createFilterOptions();
    this.currentSortColumn = '';

    this.filterState.filter = null;
    this.filterState.sortColumn = null;

    this.completenessLevels = [];
    this.filterState.completenessLevels = [];

    this.renderUsers();
    this.filterAmount = 0;
  }

  applyFilters() {
    this.filterState.filter = { ...this.filter };
    this.filterState.sortColumn = this.currentSortColumn;

    this.renderUsers();
    this.countFilterAmount();
    this.openFilter = false;
  }

  onToggleMenu(userId: string) {
    this.openMenuId = this.openMenuId === userId ? null : userId;
  }

  onCloseMenu() {
    this.openMenuId = null;
  }

  searchForUsers(search: string, scope: SearchScope = this.searchScope) {
    this.searchValueString = search;
    this.searchScope = scope;
    this.filterState.search = search;
    this.filterState.searchScope = scope;

    // Ohne neuen Verlaufseintrag: das Tippen soll den Zurueck-Knopf nicht fuellen.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.searchParams(),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.renderUsers();
  }

  /** Bereichswechsel: der Begriff bleibt stehen, die Liste filtert sofort im neuen Bereich. */
  onSearchScopeChange(scope: SearchScope) {
    this.searchForUsers(this.searchValueString, scope);
  }

  private searchParams(): Record<string, string | null> {
    return {
      in: this.searchScope,
      q: this.searchValueString || null,
      search: null,
    };
  }

  /** Nur die Angaben, die der gewaehlte Bereich durchsucht. */
  private searchFieldsOf(user: BeraterUebersicht): ConsultantSearchFields {
    switch (this.searchScope) {
      case 'name':
        return { names: consultantNameTexts(user) };
      case 'pos':
        return { positions: consultantPositionTexts(user) };
      case 'firma':
        return {
          companyCode: user?.firma,
          companyName:
            this.companyService.byAbbreviation(user?.firma)?.name ?? null,
        };
      case 'alle':
        return {
          names: consultantNameTexts(user),
          others: [
            ...consultantPositionTexts(user),
            ...consultantOtherTexts(user, this.vocabulary),
            ...this.createLanguageList(user),
          ],
        };
    }
  }

  getBeraterUebersicht() {
    forkJoin({
      users: this.userOverviewService.query(),
      companies: this.companyService.load(),
    }).subscribe({
      next: ({ users }) => {
        const normalized = this.normalizeFirma(users.body);
        this.allUsers = normalized;
        this.initialUsers = [...normalized];
        this.recomputeCompleteness();
        this.resultsLoaded = true;
        this.renderUsers();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.messageService.error(err.message);
      },
    });
  }

  onArchiveClick(value: boolean) {
    this.archiveMode = value;
    this.loading = true;
    const users$ = this.archiveMode
      ? this.userOverviewService.query([], true)
      : this.userOverviewService.query();
    forkJoin({
      users: users$,
      companies: this.companyService.load(),
    }).subscribe({
      next: ({ users }) => {
        const normalized = this.normalizeFirma(users.body);
        this.allUsers = normalized;
        this.initialUsers = [...normalized];
        this.recomputeCompleteness();
        this.resultsLoaded = true;
        this.renderUsers();
        this.loading = false;
      },
      error: (err) => {
        this.messageService.error(err.message);
        this.loading = false;
      },
    });
  }

  // Die Firma haengt am Benutzer und kommt aufgeloest vom BE — hier nur noch auf die
  // Abkuerzung normalisiert, weil das Feld als UUID oder als Abkuerzung ankommen kann.
  // Kein Rueckgriff auf den Betreuer: der kann mehrere Firmen fuehren und liefert
  // dann eine beliebige davon.
  private normalizeFirma(users: BeraterUebersicht[]): BeraterUebersicht[] {
    return users.map((u) => ({
      ...u,
      firma: this.companyService.resolveAbbreviation(u.firma),
    }));
  }

  archiveThisPofile(id: string) {
    const berater = this.users.find((u) => u.id === id);
    if (!berater) return;
    const newVersteckt = !berater.versteckt;
    this.userService.updateVersteckt(newVersteckt, berater.id).subscribe({
      next: () => {
        berater.versteckt = newVersteckt;
        if (!this.archiveMode) {
          this.allUsers = this.allUsers.filter((u) => u.id !== id);
          this.initialUsers = this.initialUsers.filter((u) => u.id !== id);
          this.renderUsers();
        }
      },
      error: (err) => {
        this.messageService.error(err.message);
      },
    });
  }

  onListView(value: boolean) {
    this.listView = value;
  }

  onOpened(event: boolean) {
    this.openFilter = event;
  }

  getSkills() {
    this.skillService.query().subscribe({
      next: (value) => {
        this.allSkills = value.content;
        this.sortVocabularyOptions();
      },
      error: (err) => {
        this.messageService.error(err.message);
      },
    });
  }

  getCertificates() {
    this.requestsService
      .getRequests(0, 1, RequestType.CERTIFICATE, true)
      .subscribe((value) => {
        this.requestsService
          .getRequests(
            0,
            value.page.totalElements,
            RequestType.CERTIFICATE,
            true,
          )
          .subscribe({
            next: (value) => {
              // Derselbe Name kann mehrfach im Katalog stehen; die Auswahlliste
              // fuehrt ihn einmal — sonst doppelte Eintraege und doppelte
              // track-Schluessel im Auswahlfeld (NG0955).
              this.allCertificates = [
                ...new Set(value.content.map((item) => item.name)),
              ];
            },
            error: (err) => {
              this.messageService.error(err.message);
            },
          });
      });
  }

  private getCatalogNames() {
    combineLatest([
      this.requestsService.getApproved$(RequestType.INDUSTRY),
      this.requestsService.getApproved$(RequestType.MAINFOCUS),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([industries, mainFocuses]) => {
        this.allIndustries = this.toOptions(industries);
        this.allMainFocuses = this.toOptions(mainFocuses);
        this.sortVocabularyOptions();
        this.knownIndustryNames = new Set(industries.map((r) => r.name));
        this.knownMainFocusNames = new Set(mainFocuses.map((r) => r.name));
        this.recomputeCompleteness();
        this.renderUsers();
      });
  }

  private toOptions(requests: Requests[]): Requests[] {
    return requests
      .map((r) => ({ id: r.id?.toString(), name: r.name?.toString(), nameEn: r.nameEn?.toString() }))
      .filter((x) => x.id && x.name);
  }

  /** Neue Arrays, damit die OnPush-Auswahllisten nach einem Sprachwechsel neu sortiert zeichnen. */
  private sortVocabularyOptions(): void {
    const language = this.vocabulary.language;
    this.allIndustries = this.vocabulary.sort(this.allIndustries, (r) => vocabularyName(r, language));
    this.allMainFocuses = this.vocabulary.sort(this.allMainFocuses, (r) => vocabularyName(r, language));
    this.allSkills = this.vocabulary.sort(this.allSkills ?? [], (s) => this.vocabulary.skillName(s));
  }

  getTrainings() {
    this.requestsService
      .getRequests(0, 1, RequestType.TRAINING, true)
      .subscribe((value) => {
        this.requestsService
          .getRequests(0, value.page.totalElements, RequestType.TRAINING, true)
          .subscribe({
            next: (value) => {
              this.allTrainings = value.content
                .map((r: any) => ({
                  id: r.id.toString(),
                  name: r.name.toString(),
                }))
                .filter((x) => x.id && x.name);
            },
            error: (err) => {
              this.messageService.error(err.message);
            },
          });
      });
  }

  getCatalog() {
    this.catalogeService.query().subscribe({
      next: (data) => {
        this.allCatalogues = data.body;
      },
      error: (err) => {
        this.messageService.error(err.message);
      },
    });
  }

  onSort(event: string) {
    this.currentSortColumn = event || '';
    this.filterState.sortColumn = event || null;
    this.renderUsers();
  }

  countFilterAmount() {
    this.filterAmount = Object.values(this.filter).filter((value) => {
      if (typeof value === 'boolean') return false;
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    }).length;
  }

  getAvailableClass(dateString: string): string {
    const today = new Date();
    const date = new Date(dateString);

    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return 'user-information__available';
    } else if (diffDays <= 28) {
      return 'user-information__available-soon';
    } else {
      return 'user-information__not-available';
    }
  }
}
